import { Injectable } from '@angular/core';
import { I18nService } from '@core/services';
import { Store } from '@ngrx/store';
import { AUTH_SING_UP, AUTH_UPDATE_FORM, STORE_ARGENTINA_FIND } from '@store/actions';
import { ComponentEnum, IdentificationTypeEnum, LanguajeEnum } from '@core/enums';
import {
	selectAuthLoading,
	selectLanguaje,
	selectRegistered,
	selectStore,
	selectStoreArgentina,
	selectStoreArgentinaLoading,
	selectStoreLoading,
} from '@store/selectors';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { ISignupStrategyComponent, ISignupStrategyPresenter } from './interfaces';
import { AuthModalComponentEnum } from '../../../enums';
import { debounceTime, distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TermsConditionsComponent } from '@shared/components';
import { IPasswordRequirementSignup } from './interfaces/password-requirement.interface';
import { IAuthModalCallback } from '../../../interfaces';

@Injectable({ providedIn: 'root' })
export class SignupStrategyPresenter
	extends IAbstractPresenter<ISignupStrategyComponent>
	implements ISignupStrategyPresenter
{
	formGroup: FormGroup;
	loading$: Observable<boolean>;
	loadingStore$: Observable<boolean>;
	storeArgentinaLoading$: Observable<boolean>;
	registered$: Observable<boolean>;
	isArgentina: boolean = false;
	translations: ILanguageObject = {};
	languageEnum$: Observable<LanguajeEnum>;
	identificationTypeEnums: IdentificationTypeEnum[] = Object.values(IdentificationTypeEnum);
	passwordRequirements: IPasswordRequirementSignup = {
		minLength: false,
		hasUpperCase: false,
		hasLowerCase: false,
		hasNumber: false,
		hasSpecialChar: false,
	};
	AuthModalComponentEnum = AuthModalComponentEnum;
	visitingCheckout: boolean = false;
	callback!: IAuthModalCallback;

	constructor(
		private store: Store,
		private i18nService: I18nService,
		private formBuilder: FormBuilder,
		private modalController: ModalController
	) {
		super();

		this.loading$ = this.store.select(selectAuthLoading);
		this.loadingStore$ = this.store.select(selectStoreLoading);
		this.languageEnum$ = this.store.select(selectLanguaje);
		this.loadingStore$ = this.store.select(selectStoreLoading);
		this.storeArgentinaLoading$ = this.store.select(selectStoreArgentinaLoading);
		this.registered$ = this.store.select(selectRegistered);

		this.formGroup = this.formBuilder.group(
			{
				email: [
					null,
					[
						Validators.required,
						Validators.email,
						Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
					],
				],
				name: [
					null,
					[
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(50),
						Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/),
					],
				],
				lastName: [
					null,
					[
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(50),
						Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/),
					],
				],
				phone: [null, [Validators.required, this.phoneDigitsValidator.bind(this)]],
				password: [null, [Validators.required, Validators.minLength(8), this.validatePasswordStrength.bind(this)]],
				confirmPassword: [null, [Validators.required]],
				termsAccepted: [false, Validators.requiredTrue],
				identityCardType: [IdentificationTypeEnum.Cedula],
				identityCard: [
					null,
					[Validators.required, Validators.pattern(/^[0-9]+$/), Validators.minLength(11), Validators.maxLength(11)],
				],
			},
			{
				validators: this.passwordMatchValidator,
			}
		);
	}

	override onInit(): void {
		this.destroy$ = new Subject<void>();
		this.formGroup.reset({
			termsAccepted: false,
			identityCardType: IdentificationTypeEnum.Cedula,
		});
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.languageEnum$.pipe(takeUntil(this.destroy$)).subscribe(lang => {
			const trans = this.i18nService.getTranslations(ComponentEnum.Auth, lang);
			this.translations = trans;
			this.view.translations = trans;
		});

		this.store
			.select(selectStore)
			.pipe(takeUntil(this.destroy$))
			.subscribe(store => {
				this.view.isArgentina = store?.isArgentina ?? false;
				if (!this.view.isArgentina) {
					const identityControl = this.formGroup.get('identityCard');
					identityControl?.clearValidators();
					identityControl?.setErrors(null);
					identityControl?.updateValueAndValidity({ emitEvent: false });
				}
			});

		this.formGroup
			.get('identityCard')
			?.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$))
			.subscribe(() => {
				if (
					this.formGroup.get('identityCard')?.valid &&
					this.formGroup.get('identityCardType')?.value === IdentificationTypeEnum.Cuit
				) {
					this.store.dispatch(
						STORE_ARGENTINA_FIND({
							cuit: this.formGroup.get('identityCard')?.value as string,
						})
					);
				}
			});

		this.store
			.select(selectStoreArgentina)
			.pipe(takeUntil(this.destroy$))
			.subscribe(storeArgentina => {
				if (storeArgentina) {
					this.formGroup.patchValue({
						lastName: storeArgentina?.apellido,
						name: storeArgentina?.nombre,
					});
				}
			});

		this.formGroup.valueChanges
			.pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe(value => {
				if (value.confirmPassword !== value.password)
					this.formGroup.get('confirmPassword')?.setErrors({ passwordMismatch: true }, { emitEvent: false });
				else this.formGroup.get('confirmPassword')?.setErrors(null, { emitEvent: false });

				this.store.dispatch(AUTH_UPDATE_FORM({ authForm: { ...value } }));
			});

		this.registered$.pipe(takeUntil(this.destroy$)).subscribe(registered => {
			if (registered) {
				this.navigateTo(AuthModalComponentEnum.LOGIN);
			}
		});
	}

	onSignUp(): void {
		if (this.formGroup.valid) this.store.dispatch(AUTH_SING_UP());
	}

	async openTermsModal(): Promise<void> {
		const modal = await this.modalController.create({
			component: TermsConditionsComponent,
			cssClass: 'terms-conditions-modal',
			componentProps: {},
		});

		modal.onDidDismiss().then(data => {
			if (data.role === 'accepted') {
				this.formGroup.get('termsAccepted')?.setValue(true);
			}
		});

		await modal.present();
	}

	formatPhone(event: Event): void {
		let { value } = event.target as HTMLInputElement;
		let input = value.replace(/\D/g, '').substring(0, 10);
		let area = input.substring(0, 3);
		let middle = input.substring(3, 6);
		let last = input.substring(6, 10);

		if (input.length > 6) {
			value = `(${area}) ${middle}-${last}`;
		} else if (input.length > 3) {
			value = `(${area}) ${middle}`;
		} else if (input.length > 0) {
			value = `(${area}`;
		}

		this.formGroup.get('phone')?.setValue(value, { emitEvent: false });
	}

	isFieldInvalid(fieldName: string): boolean {
		const control = this.formGroup.get(fieldName);
		return control?.errors !== null && control?.value !== null && control?.value !== '';
	}

	getFieldErrorMessage(fieldName: string): string {
		const control = this.formGroup.get(fieldName);
		if (!control?.errors || !control.value) return '';

		switch (fieldName) {
			case 'email':
				if (control.errors['required']) return this.translations['emailRequired'];
				if (control.errors['email'] || control.errors['pattern']) return this.translations['emailInvalid'];
				break;
			case 'name':
				if (control.errors['required']) return this.translations['nameRequired'];
				if (control.errors['minlength']) return this.translations['nameMinLength'];
				if (control.errors['maxlength']) return this.translations['nameMaxLength'];
				if (control.errors['pattern']) return this.translations['namePattern'];
				break;
			case 'lastName':
				if (control.errors['required']) return this.translations['lastNameRequired'];
				if (control.errors['minlength']) return this.translations['lastNameMinLength'];
				if (control.errors['maxlength']) return this.translations['lastNameMaxLength'];
				if (control.errors['pattern']) return this.translations['lastNamePattern'];
				break;
			case 'phone':
				if (control.errors['required']) return this.translations['phoneRequired'];
				if (control.errors['phone']) return this.translations['phonePattern'];
				break;
			case 'identityCard':
				if (control.errors['required']) return this.translations['identityCardRequired'];
				if (control.errors['pattern']) return this.translations['identityCardPattern'];
				if (control.errors['minlength']) return this.translations['identityCardMinLength'];
				if (control.errors['maxlength']) return this.translations['identityCardMaxLength'];
				break;
		}
		return '';
	}

	getFieldErrorMessages(fieldName: string): string[] {
		const control = this.formGroup.get(fieldName);
		if (!control?.errors || !control.value) return [];
		const errors = control.errors;
		const messages: string[] = [];

		if (fieldName === 'password') {
			if (errors['required']) messages.push(this.translations['passwordRequired']);
			if (errors['minlength']) messages.push(this.translations['passwordMinLength']);
			if (errors['hasUpperCase']) messages.push(this.translations['passwordHasUpperCase']);
			if (errors['hasLowerCase']) messages.push(this.translations['passwordHasLowerCase']);
			if (errors['hasNumber']) messages.push(this.translations['passwordHasNumber']);
			if (errors['hasSpecialChar']) messages.push(this.translations['passwordHasSpecialChar']);
		}
		return messages;
	}

	validatePasswordStrength(control: AbstractControl): ValidationErrors | null {
		const password = control.value;
		if (!password) return null;

		this.passwordRequirements = {
			minLength: password.length >= 8,
			hasUpperCase: /[A-Z]/.test(password),
			hasLowerCase: /[a-z]/.test(password),
			hasNumber: /[0-9]/.test(password),
			hasSpecialChar: /[@$!%*?&#+\-_]/.test(password),
		};

		const errors: ValidationErrors = {};
		if (!this.passwordRequirements.minLength) errors['minLength'] = true;
		if (!this.passwordRequirements.hasUpperCase) errors['hasUpperCase'] = true;
		if (!this.passwordRequirements.hasLowerCase) errors['hasLowerCase'] = true;
		if (!this.passwordRequirements.hasNumber) errors['hasNumber'] = true;
		if (!this.passwordRequirements.hasSpecialChar) errors['hasSpecialChar'] = true;

		return Object.keys(errors).length ? errors : null;
	}

	passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
		const password = group.get('password');
		const confirmPassword = group.get('confirmPassword');
		if (!password || !confirmPassword) return null;
		return password.value === confirmPassword.value ? null : { passwordMismatch: true };
	}

	phoneDigitsValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value || '';
		const digits = value.replace(/\D/g, '');
		return digits.length === 10 ? null : { phone: true };
	}

	navigateTo(route: AuthModalComponentEnum): void {
		this.view.callback?.navigate(route);
	}
}
